//import id from 'date-fns/esm/locale/id/index.js';
import { useRouter } from "@tanstack/react-router";
import React, { createContext, useContext } from "react";

import api from "../services/api";

interface ExpedContextData {
  handleConfirmTolerance: ({ companyId }: any) => void;
  handleConfirmDelay: ({ delayValue, delayTime, companyId }: any) => void;

  getDelay: ({ setDelayStatus, setDelayTime, setDelayedTime, setDelayName, setIconName, companyId }: any) => void;
  queryQueue: ({
    shippingCompanyId,
    companyId,
    setQueuesByDays,
    setQueuesByDaysFilter,
    setQueues,
    setDays,
    setDates,
    setActive,
    setTotals,
    setAttendanceLimit,
    setLoading,
    returnRowsByDay
  }: any) => Promise<void>;
  handleConfirmSuspend: ({ attendance, userId, companyId, setIsOpenDisableToleranceModal }: any) => void;

  handleConfirmDelayCancelation: ({ setDelayTime, setDelayStatus, minutes, delayedTime, delayTime, delayValue, companyId }: any) => void;
  handleReturnQueue: ({ id, hideModal, active, queryQueue, setActive }: any) => void;
}

const ExpedContext = createContext<ExpedContextData>({} as ExpedContextData);

interface ExpedProviderProps {
  children: React.ReactNode;
}

function ExpedProvider({ children }: ExpedProviderProps) {
  const router = useRouter();

  const getDelay = ({ setDelayStatus, setDelayTime, setDelayedTime, setDelayName, setIconName, companyId }: any) => {
    if (companyId) {
      try {
        api.get(`queue/delayStatus/company/${companyId}`).then((response) => {
          const { data } = response;

          if (data) {
            const { delayedTime, delay, expedition, created_at } = data;
            const todayInMilliseconds = new Date().getTime();

            setDelayStatus(true);
            setDelayTime(delayedTime);
            setDelayedTime(0);
            setDelayName(delay.reason);
            setIconName(delay.icon);

            let timer = todayInMilliseconds - new Date(expedition).getTime();

            if (isNaN(timer)) {
              const date = new Date(created_at);

              timer = todayInMilliseconds - date.getTime();

              const time = Math.round(Math.floor(timer / (60 * 1000)));
              setDelayedTime(time);
            }
          }
        });
      } catch (error) {
        console.log(error);
      }
    }
  };

  function onlyUnique<T>(value: T, index: number, self: T[]) {
    return self.indexOf(value) === index;
  }

  async function queryQueue({
    shippingCompanyId,
    companyId,
    ////estados
    setQueuesByDays,
    setQueuesByDaysFilter,
    setQueues,
    setDays,
    setDates,
    setActive,
    ///estados de outras reqs
    setTotals,
    setAttendanceLimit,
    setLoading,
    //funcs
    returnRowsByDay
  }: any) {
    const { data } = await api.get(`/queue/shipping-company/${shippingCompanyId}/positions`);
    const _queuePositions = data;

    const route = shippingCompanyId ? `/queue/shipping-company/${shippingCompanyId}` : `/queue/company/${companyId}`;
    const activeRoute = shippingCompanyId ? `/queue/active/shipping-company/${shippingCompanyId}` : `/queue/active/company/${companyId}`;

    const { data: _queue } = await api.get<any[]>(route, {
      params: {
        days_limit: 5
      }
    });

    await api.get(`${activeRoute}`).then((response) => {
      let _active = [];

      if (response.data[0]) {
        setActive(response.data);
        _active = response.data;
      }

      const _days: string[] = [];
      const _dates: string[] = [];

      for (let index = 0; index < _queue.length; index++) {
        const date = `${new Date(_queue[index].date).getDate()}`;
        _days.push(date);
      }

      for (let index = 0; index < _queue.length; index++) {
        const date = `${new Date(_queue[index].date).getDate()}/${new Date(_queue[index].date).getMonth() + 1}`;
        _dates.push(date);
      }

      const _queuesByDays = [];
      const unique = _days.filter(onlyUnique);
      const uniqueDates = _dates.filter(onlyUnique);

      for (let index = 0; index < unique.length; index++) {
        _queuesByDays.push(returnRowsByDay(_queue, unique[index], _active, _queuePositions));
      }

      setQueuesByDays(_queuesByDays);
      setQueuesByDaysFilter(_queuesByDays);
      setQueues(_queue);
      setDays(unique);
      setDates(uniqueDates);
    });

    await api.get(`/queue/totals/company/${companyId}`).then((response) => {
      setTotals({
        total: response.data.totals,
        attended: response.data.attended,
        missing: response.data.missing,
        totalToday: response.data.totals,
        totalTransportedToday: response.data.totalTransportedToday.toString().split(".")
      });
    });

    await api.get(`/settings/company/${companyId}`).then((response) => {
      setAttendanceLimit(response.data.programmedTotal);
    });

    setLoading(false);
  }

  function handleConfirmSuspend({ attendance, userId, companyId, setIsOpenDisableToleranceModal }: any) {
    if (!attendance) {
      api
        .post(`/attendances/save`, {
          user: { id: Number(userId) },
          company: { id: Number(companyId) }
        })
        .then(() => {
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        });
    } else {
      setIsOpenDisableToleranceModal(true);
    }
  }

  function handleConfirmTolerance({ companyId }: any) {
    api
      .post("settings/toggle-tolerance", {
        company: companyId,
        tolerance: true
      })
      .then(() => {
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      });
  }

  function handleConfirmDelay({ delayValue, delayTime, companyId }: any) {
    api
      .put(`/queue/delay`, {
        delayId: delayValue.value,
        time: delayTime,
        companyId: companyId
      })
      .then((response) => {
        if (response.status === 200) {
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        }
      })
      .catch(() => {});
  }

  async function handleConfirmDelayCancelation({ setDelayTime, setDelayStatus, minutes, delayedTime, delayTime, delayValue, companyId }: any) {
    const subTime = minutes + delayedTime;
    const delayTimeMinutes = parseInt(delayTime) * 60;
    const remainingTime = delayTimeMinutes - subTime;

    const num = remainingTime;
    const hours = num / 60;
    const rhours = Math.floor(hours);
    const sminutes = (hours - rhours) * 60;
    const rminutes = Math.round(sminutes);
    let delayedTimeMinutes = "0";

    rminutes < 10 ? (delayedTimeMinutes = `0${rminutes}`) : (delayedTimeMinutes = `${rminutes}`);
    const delayedTimeHours = `-0${rhours}:${delayedTimeMinutes}`;

    await api.put(`/queue/delay`, {
      delayId: delayValue.value,
      time: delayedTimeHours,
      companyId: companyId
    });

    setDelayTime(delayedTimeHours);
    setDelayStatus(false);
    router.invalidate();
  }

  function handleReturnQueue({ id, hideModal, active, setActive }: any) {
    hideModal();

    api
      .put(`/queue/start`, {
        id: id,
        status: false
      })
      .then((response) => {
        const results = active.filter((item: any) => item.id !== id);

        setActive(results);
      })
      .catch(() => {});
  }

  return (
    <ExpedContext.Provider
      value={{
        getDelay,
        queryQueue,
        handleConfirmSuspend,
        handleConfirmTolerance,
        handleConfirmDelay,
        handleConfirmDelayCancelation,
        handleReturnQueue
      }}
    >
      {children}
    </ExpedContext.Provider>
  );
}

const useExped = () => {
  const context = useContext(ExpedContext);

  return context;
};

export { ExpedProvider, useExped };
